import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  BuildingIcon,
  ChevronRightIcon,
  ClockIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ClipboardListIcon,
  FileTextIcon,
  LogOutIcon,
  MessageSquareIcon,
  ReceiptIcon,
  SettingsIcon,
  TimerIcon,
  UserIcon,
  UsersIcon,
  XIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { usePermissions } from '../hooks/usePermissions'
import { usesFieldHome } from '../lib/fieldHome'
import { mobile } from './mobile'

type MoreItem = {
  name: string
  href: string
  icon: React.ComponentType<{ className?: string }>
  permission?: string | string[]
}

interface MobileMoreMenuProps {
  open: boolean
  onClose: () => void
}

function canSee(
  canAny: (perms: string[]) => boolean,
  permission?: string | string[]
) {
  if (!permission) return true
  const perms = Array.isArray(permission) ? permission : [permission]
  return canAny(perms)
}

/** Bottom-nav duplicates — never repeat in More. */
const BOTTOM_NAV_HREFS = new Set([
  '/dashboard',
  '/jobs',
  '/time-clock',
  '/my-properties',
  '/properties',
  '/my-day',
])

export function MobileMoreMenu({ open, onClose }: MobileMoreMenuProps) {
  const { t } = useTranslation('common')
  const location = useLocation()
  const { user, profile, signOut } = useAuth()
  const { canAny, isSuperAdmin } = usePermissions()
  const fieldHome = usesFieldHome(profile?.role, isSuperAdmin)

  const userEmail = user?.email || ''
  const displayName =
    profile?.first_name || profile?.last_name
      ? `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim()
      : user?.user_metadata?.full_name ||
        user?.user_metadata?.name ||
        userEmail.split('@')[0] ||
        'User'
  const initials =
    displayName
      .split(' ')
      .map((n: string) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || 'U'

  const handleLogout = async () => {
    try {
      onClose()
      await signOut()
    } catch (err) {
      console.error('Logout failed:', err)
    }
  }

  const settingsHref = canAny(['settings.general']) ? '/settings' : '/profile'

  /** Field-home roles: flat limited list (no admin nesting, no bottom-nav dupes). */
  const fieldItems: MoreItem[] = [
    {
      name: t('common:moreMenu.expenses'),
      href: '/expenses',
      icon: ReceiptIcon,
      permission: ['expenses.submit', 'expenses.review'],
    },
    {
      name: t('common:moreMenu.properties'),
      href: '/my-properties',
      icon: BuildingIcon,
      permission: 'properties.view',
    },
    {
      name: t('common:moreMenu.timeManagement'),
      href: canAny(['time.review', 'time.review_crew'])
        ? '/time-review'
        : '/time-clock',
      icon: canAny(['time.review', 'time.review_crew']) ? TimerIcon : ClockIcon,
      permission: ['time.clock', 'time.review', 'time.review_crew'],
    },
    {
      name: t('common:moreMenu.teamChat'),
      href: '/messages',
      icon: MessageSquareIcon,
      permission: ['communication.inbox', 'communication.chat'],
    },
    {
      name: t('common:moreMenu.settingsProfile'),
      href: settingsHref,
      icon: SettingsIcon,
    },
  ]

  /** Managers: broader flat menu (light sheet), still hide bottom-nav dupes. */
  const managerItems: MoreItem[] = [
    {
      name: 'Properties',
      href: '/properties',
      icon: BuildingIcon,
      permission: 'properties.view',
    },
    {
      name: 'Customers',
      href: '/customers',
      icon: UsersIcon,
      permission: 'customers.view',
    },
    {
      name: 'Leads',
      href: '/leads',
      icon: UsersIcon,
      permission: 'leads.view',
    },
    {
      name: 'Proposals',
      href: '/proposals',
      icon: FileTextIcon,
      permission: 'proposals.view',
    },
    {
      name: 'Contracts',
      href: '/contracts',
      icon: FileTextIcon,
      permission: 'contracts.view',
    },
    {
      name: 'Recurring Jobs',
      href: '/recurring-jobs',
      icon: ClipboardListIcon,
      permission: 'jobs.view',
    },
    {
      name: 'Dispatch Calendar',
      href: '/job-calendar',
      icon: CalendarDaysIcon,
      permission: 'jobs.dispatch',
    },
    {
      name: 'Time Review',
      href: '/time-review',
      icon: TimerIcon,
      permission: ['time.review', 'time.review_crew'],
    },
    {
      name: 'Expenses',
      href: '/expenses',
      icon: ReceiptIcon,
      permission: ['expenses.submit', 'expenses.review'],
    },
    {
      name: 'Expense Review',
      href: '/expense-review',
      icon: ClipboardListIcon,
      permission: 'expenses.review',
    },
    {
      name: 'Review',
      href: '/review',
      icon: CheckCircleIcon,
      permission: 'review.view',
    },
    {
      name: 'End of Day',
      href: '/end-of-day',
      icon: CheckCircleIcon,
      permission: 'review.view',
    },
    {
      name: 'QC Visits',
      href: '/qc-visits',
      icon: CheckCircleIcon,
      permission: 'qc.view',
    },
    {
      name: 'Messages',
      href: '/messages',
      icon: MessageSquareIcon,
      permission: ['communication.inbox', 'communication.chat'],
    },
    {
      name: 'Reports',
      href: '/reports',
      icon: FileTextIcon,
      permission: 'reports.view',
    },
    {
      name: 'Billing',
      href: '/billing',
      icon: ReceiptIcon,
      permission: 'financial.billing',
    },
    {
      name: 'Settings',
      href: '/settings',
      icon: SettingsIcon,
      permission: ['settings.general', 'team.view', 'properties.edit'],
    },
    {
      name: 'Profile',
      href: '/profile',
      icon: UserIcon,
    },
  ]

  const items = (fieldHome ? fieldItems : managerItems).filter((item) => {
    const path = item.href.split('?')[0]
    // Field "Time management" may wire to Time Clock — keep it even though Clock is on the tab bar.
    if (!fieldHome && BOTTOM_NAV_HREFS.has(path)) return false
    if (fieldHome && BOTTOM_NAV_HREFS.has(path) && item.name !== t('common:moreMenu.timeManagement')) {
      return false
    }
    return canSee(canAny, item.permission)
  })

  if (!open) return null

  const isActive = (href: string) => {
    const path = href.split('?')[0]
    if (path === '/dashboard') {
      return location.pathname === '/' || location.pathname === '/dashboard'
    }
    return location.pathname === path || location.pathname.startsWith(path + '/')
  }

  return (
    <div className="fixed inset-0 z-50 flex">
      <button
        type="button"
        className="absolute inset-0 bg-black/40 backdrop-blur-[1px]"
        aria-label="Close menu"
        onClick={onClose}
      />
      <div
        className="relative z-10 h-full w-[min(20rem,90vw)] bg-gray-50 shadow-2xl flex flex-col animate-[slideInLeft_180ms_ease-out]"
        style={{
          paddingTop: 'env(safe-area-inset-top)',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
        role="dialog"
        aria-modal="true"
        aria-label={t('common:moreMenu.title')}
      >
        <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
              <span className="text-white text-sm font-semibold">{initials}</span>
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">{displayName}</p>
              <p className="text-xs text-gray-500 truncate">{userEmail}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={mobile.ghostBtn}
            aria-label={t('common:close')}
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-4">
          <div>
            <p className={`${mobile.sectionLabel} mb-1.5 px-1`}>{t('common:moreMenu.title')}</p>
            <div className={mobile.listGroup}>
              {items.map((item) => {
                const Icon = item.icon
                const active = isActive(item.href)
                return (
                  <Link
                    key={`${item.name}-${item.href}`}
                    to={item.href}
                    onClick={onClose}
                    className={`${mobile.row} flex items-center gap-3 ${
                      active ? 'bg-blue-50' : ''
                    }`}
                  >
                    <span
                      className={`inline-flex h-9 w-9 items-center justify-center rounded-xl ${
                        active
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                    </span>
                    <span
                      className={`flex-1 text-sm font-medium truncate ${
                        active ? 'text-blue-800' : 'text-gray-900'
                      }`}
                    >
                      {item.name}
                    </span>
                    <ChevronRightIcon className="w-4 h-4 text-gray-300 flex-shrink-0" />
                  </Link>
                )
              })}
            </div>
          </div>
        </nav>

        <div className="px-3 pb-3 pt-1 border-t border-gray-200 bg-white">
          <button
            type="button"
            onClick={handleLogout}
            className={`${mobile.row} flex items-center gap-3 w-full text-red-600`}
          >
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-red-50 text-red-600">
              <LogOutIcon className="w-4 h-4" />
            </span>
            <span className="text-sm font-semibold">{t('common:moreMenu.signOut')}</span>
          </button>
        </div>
      </div>
    </div>
  )
}
